import type { LayoutResult } from './layout-treemap';
import {
  buildParentMap,
  buildTreemapLayout,
  collectSubtreeIds,
  findTreemapNodeById,
  getAncestorPath,
} from './layout-treemap';
import { BASE_HEIGHT, BASE_WIDTH } from './treemap-constants';
import { EMPTY_ACTIVE_IDS } from './treemap-hover-context';
import { TreemapNodeKind, type ParsedTreemap, type TreemapFlowNode } from './treemap-types';

const EMPTY_TREEMAP_NODES: TreemapFlowNode[] = [];

export interface TreemapViewportRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface GetActiveNodeIdsInput {
  hoveredNodeId: string | null;
  parsed: ParsedTreemap | null;
  parentMap: Map<string, string>;
}

interface GetSectionDrillPathInput {
  parsed: ParsedTreemap | null;
  parentMap: Map<string, string>;
  nodeId: string;
}

export function getTreemapLayout(parsed: ParsedTreemap | null): LayoutResult | null {
  if (!parsed) {
    return null;
  }

  return buildTreemapLayout({
    root: parsed.root,
    width: BASE_WIDTH,
    height: BASE_HEIGHT,
    classDefs: parsed.classDefs,
  });
}

export function getTreemapParentMap(parsed: ParsedTreemap | null): Map<string, string> {
  if (!parsed) {
    return new Map<string, string>();
  }

  return buildParentMap(parsed.root);
}

export function getTreemapLayoutNodes(layout: LayoutResult | null): TreemapFlowNode[] {
  return layout?.nodes ?? EMPTY_TREEMAP_NODES;
}

export function getTreemapTotalValue(parsed: ParsedTreemap | null): number {
  return parsed?.root.value ?? 0;
}

export function hasRenderableTreemapLayout(layout: LayoutResult | null): layout is LayoutResult {
  return Boolean(layout && layout.nodes.length > 0);
}

export function getTreemapRectById(nodes: TreemapFlowNode[]): Map<string, TreemapViewportRect> {
  return new Map(
    nodes.map((node) => [
      node.id,
      {
        x: node.position.x,
        y: node.position.y,
        width: node.width ?? 0,
        height: node.height ?? 0,
      },
    ]),
  );
}

export function getTreemapActiveNodeIds({
  hoveredNodeId,
  parsed,
  parentMap,
}: GetActiveNodeIdsInput): Set<string> {
  if (!hoveredNodeId || !parsed) {
    return EMPTY_ACTIVE_IDS;
  }

  const ids = new Set<string>();
  const hoveredNode = findTreemapNodeById({ root: parsed.root, nodeId: hoveredNodeId });
  if (hoveredNode) {
    collectSubtreeIds(hoveredNode).forEach((id) => ids.add(id));
  }
  getAncestorPath({ nodeId: hoveredNodeId, parentMap, rootId: parsed.root.id }).forEach((id) =>
    ids.add(id),
  );

  return ids;
}

export function getTreemapSectionDrillPath({
  parsed,
  parentMap,
  nodeId,
}: GetSectionDrillPathInput): string[] | null {
  if (!parsed) {
    return null;
  }

  const node = findTreemapNodeById({ root: parsed.root, nodeId });
  if (!node || node.kind !== TreemapNodeKind.Section) {
    return null;
  }

  return getAncestorPath({ nodeId, parentMap, rootId: parsed.root.id }).reverse();
}

export function isTreemapViewportRect(rect: TreemapViewportRect | undefined): rect is TreemapViewportRect {
  if (!rect) {
    return false;
  }

  return Number.isFinite(rect.x) && Number.isFinite(rect.y) && rect.width > 0 && rect.height > 0;
}

export function getDrillPathTargetId(drillPathIds: string[]): string | null {
  return drillPathIds[drillPathIds.length - 1] ?? null;
}
